// AuthContext + useAuth hook —— 全局 session 状态管理。
//
// session token 存 localStorage（Tauri 端 H5 后可换 OS keychain）；
// 启动时若有 token 自动调 /auth/me 验证，成功则进入登录态。

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  fetchMe,
  logout as apiLogout,
  passwordLogin,
  passwordSignup,
  uploadMyPubkey,
  type AuthUser,
} from "./auth-api";
import {
  bytesToBase64,
  defaultDeviceLabel,
  ensureIdentityKey,
} from "./identity-key";
import { requestNotifyPermission } from "./notification";

const SESSION_KEY = "chatbox.session";

export type AuthStatus =
  | "loading" // 初始化中（在验证已存 token）
  | "anonymous" // 未登录
  | "authenticated"; // 已登录

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  session: string | null;
  /** 用 session token 完成登录（来自 magic-link 回调） */
  signInWithSession(session: string): Promise<void>;
  /** 邮箱密码注册并自动登录 */
  signUpWithPassword(email: string, password: string): Promise<void>;
  /** 邮箱密码登录 */
  signInWithPassword(email: string, password: string): Promise<void>;
  /** 注销当前 session（服务端 + 本地） */
  signOut(): Promise<void>;
  /** K3+ 直接更新内存中的 user（Settings 改昵称后用） */
  setUser(user: AuthUser): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  // 启动时：从 localStorage 恢复 + 校验
  useEffect(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (!saved) {
      setStatus("anonymous");
      return;
    }
    let alive = true;
    fetchMe(saved)
      .then(({ user }) => {
        if (!alive) return;
        setSession(saved);
        setUser(user);
        setStatus("authenticated");
        // 启动时也跑一次（已登录场景）
        void ensurePubkeyUploaded(saved);
      })
      .catch(() => {
        if (!alive) return;
        localStorage.removeItem(SESSION_KEY);
        setStatus("anonymous");
      });
    return () => {
      alive = false;
    };
  }, []);

  const applyAuth = useCallback((nextSession: string, nextUser: AuthUser) => {
    localStorage.setItem(SESSION_KEY, nextSession);
    setSession(nextSession);
    setUser(nextUser);
    setStatus("authenticated");
    void ensurePubkeyUploaded(nextSession);
    // K8: 顺手请求通知权限 (浏览器只在用户手势后才弹)
    void requestNotifyPermission();
  }, []);

  const signInWithSession = useCallback(
    async (newSession: string) => {
      const { user } = await fetchMe(newSession);
      applyAuth(newSession, user);
    },
    [applyAuth],
  );

  const signUpWithPassword = useCallback(
    async (email: string, password: string) => {
      const { user, session: newSession } = await passwordSignup(email, password);
      applyAuth(newSession, user);
    },
    [applyAuth],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const { user, session: newSession } = await passwordLogin(email, password);
      applyAuth(newSession, user);
    },
    [applyAuth],
  );

  const signOut = useCallback(async () => {
    if (session) {
      try {
        await apiLogout(session);
      } catch {
        /* 即使服务端失败也清本地 */
      }
    }
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(PUBKEY_UPLOADED_FLAG);
    setSession(null);
    setUser(null);
    setStatus("anonymous");
  }, [session]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      session,
      signInWithSession,
      signUpWithPassword,
      signInWithPassword,
      signOut,
      setUser,
    }),
    [
      status,
      user,
      session,
      signInWithSession,
      signUpWithPassword,
      signInWithPassword,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}

// 启动时 / 登录后：确保本机 Ed25519 pubkey 已上传到 server。
// best-effort，失败仅 console.warn（用户能用 ChatBox 普通功能，
// 只是审批流程暂不可用，等下次登录重试）。
const PUBKEY_UPLOADED_FLAG = "chatbox.pubkey_uploaded";
async function ensurePubkeyUploaded(session: string): Promise<void> {
  try {
    const { publicKey } = await ensureIdentityKey();
    const b64 = bytesToBase64(publicKey);
    // 同一 session + 同一 pubkey 已经上传过 → 跳过（避免每次启动都打一次）
    const cached = localStorage.getItem(PUBKEY_UPLOADED_FLAG);
    if (cached === b64) return;
    await uploadMyPubkey(session, b64, defaultDeviceLabel());
    localStorage.setItem(PUBKEY_UPLOADED_FLAG, b64);
  } catch (e) {
    console.warn("[F3] pubkey upload failed (will retry next login):", e);
  }
}
